/**
 * Revenue Target Dashboard
 *
 * Quarterly revenue target vs actuals: target derived from a net-profit
 * margin goal over the estimated cost base, tracked with a progress bar,
 * monthly breakdown and editable assumptions.
 */

import { useState, useCallback } from 'react';
import type { ChangeEvent } from 'react';
import { useRevenueTarget } from '@/hooks/useRevenueTarget';
import { CommentButton } from './CommentButton';
import { RevenueTargetSettings } from './RevenueTargetSettings';
import { ProfitScenariosSection } from './ProfitScenariosSection';
import { CostPerPersonSection } from './CostPerPersonSection';
import { AssetPurchaseSection } from './AssetPurchaseSection';
import { BillingEstimateHistorySection } from './BillingEstimateHistorySection';
import { formatCurrency } from '@/utils/formatters';
import type { QuarterMonth, TargetTracking } from '@/types/revenueTarget';
import './RevenueTargetDashboard.css';

/** Days since an upstream snapshot cutoff beyond which it reads as stale */
const ESTIMATE_STALE_AFTER_DAYS = 2;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function estimateAgeDays(asOf: string): number {
  // Parse as UTC so the stale indicator cannot flip with the viewer's
  // locale or DST offset.
  const age = (Date.now() - new Date(`${asOf}T00:00:00Z`).getTime()) / MS_PER_DAY;
  return Number.isFinite(age) ? Math.floor(age) : 0;
}

const STATUS_META: Record<
  TargetTracking['status'],
  { label: string; className: string }
> = {
  ahead: { label: 'Ahead of pace', className: 'rt-status--ahead' },
  'on-track': { label: 'On track', className: 'rt-status--on-track' },
  behind: { label: 'Behind pace', className: 'rt-status--behind' },
};

/** Sentinel select value meaning "whatever quarter we are in today" */
const CURRENT_QUARTER = 'current';

const MONTH_STATUS_LABEL: Record<QuarterMonth['status'], string> = {
  actual: 'Actual',
  partial: 'Month to date',
  estimated: 'Estimated',
};

export function RevenueTargetDashboard() {
  const {
    data,
    loading,
    error,
    saving,
    saveError,
    quarter: selectedQuarter,
    selectQuarter,
    refresh,
    saveConfig,
  } = useRevenueTarget();

  // Bumped after a successful config save so the Profit Scenarios
  // section refetches with the new target margin.
  const [scenarioRefreshToken, setScenarioRefreshToken] = useState(0);

  const handleQuarterChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      selectQuarter(e.target.value === CURRENT_QUARTER ? null : e.target.value);
    },
    [selectQuarter]
  );

  const handleSaveConfig = useCallback(
    async (input: Parameters<typeof saveConfig>[0]): Promise<boolean> => {
      const ok = await saveConfig(input);
      if (ok) setScenarioRefreshToken((t) => t + 1);
      return ok;
    },
    [saveConfig]
  );

  if (loading && !data) {
    return (
      <div className="rt-loading">
        <div className="loading-spinner"></div>
        <p>Calculating revenue target from Xero data...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rt-error" role="alert">
        <h2>Unable to load revenue target</h2>
        <p>{error}</p>
        <button onClick={refresh} className="rt-error__retry">
          Try Again
        </button>
        {selectedQuarter && (
          <button
            onClick={() => selectQuarter(null)}
            className="rt-error__retry"
          >
            Back to current quarter
          </button>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { quarter, availableQuarters, tracking, costBasis, months, config } =
    data;
  const isHistoric = quarter.isComplete;
  const varianceToTarget = tracking.qtdRevenue - tracking.targetRevenue;
  const status = STATUS_META[tracking.status];
  const progressWidth = Math.min(100, tracking.revenueProgressPercent);
  const timeMarker = Math.min(100, tracking.timeElapsedPercent);
  const targetCoversMargin =
    tracking.targetRevenue >= tracking.requiredRevenueForMargin;
  const actualMarginPercent =
    tracking.qtdRevenue > 0
      ? (tracking.qtdNetProfit / tracking.qtdRevenue) * 100
      : 0;

  return (
    <div className="rt-dashboard">
      <header className="rt-header">
        <div>
          <h2 className="rt-header__title">
            Revenue Target
            <CommentButton targetType="metric" targetId="revenue-target" />
          </h2>
          <p className="rt-header__subtitle">
            {quarter.label} &middot;{' '}
            {isHistoric
              ? 'completed quarter'
              : `${quarter.daysRemaining} days remaining`}
          </p>
        </div>
        <div className="rt-header__controls">
          <label className="rt-quarter-picker">
            <span className="rt-quarter-picker__label">Quarter</span>
            <select
              value={selectedQuarter ?? CURRENT_QUARTER}
              onChange={handleQuarterChange}
              disabled={loading}
              className="rt-quarter-picker__select"
            >
              {availableQuarters.map((q) => (
                <option
                  key={q.key}
                  value={q.isCurrent ? CURRENT_QUARTER : q.key}
                >
                  {q.shortLabel}
                  {q.isCurrent ? ' (current)' : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={refresh}
            disabled={loading}
            className="rt-header__refresh"
            aria-label="Refresh revenue target"
          >
            <span className={loading ? 'loading-spinner' : ''}>&#8635;</span>{' '}
            Refresh
          </button>
        </div>
      </header>

      {isHistoric && (
        <p className="rt-historic-note" role="note">
          Showing a completed quarter. Actuals are as reported by Xero; the
          target and cost assumptions come from your current settings, so they
          may differ from what was set at the time.
        </p>
      )}

      {/* Hero: target vs QTD progress */}
      <section className="rt-hero" aria-label="Target progress">
        <div className="rt-hero__numbers">
          <div className="rt-hero__block">
            <span className="rt-hero__label">Quarter target</span>
            <span className="rt-hero__value">
              {formatCurrency(tracking.targetRevenue)}
            </span>
            <span className="rt-hero__hint">
              for {config.targetNetMarginPercent}% net profit before tax
            </span>
          </div>
          <div className="rt-hero__block">
            <span className="rt-hero__label">
              {isHistoric ? 'Invoiced in quarter' : 'Invoiced quarter to date'}
            </span>
            <span className="rt-hero__value rt-hero__value--actual">
              {formatCurrency(tracking.qtdRevenue)}
            </span>
            <span className="rt-hero__hint">
              {tracking.revenueProgressPercent.toFixed(1)}% of target
            </span>
            {data.projected && (
              <span className="rt-hero__projected">
                Projected incl. unbilled:{' '}
                {formatCurrency(data.projected.projectedQtdRevenue)} (
                {data.projected.projectedProgressPercent.toFixed(1)}%)
                <span className="rt-hero__projected-hint">
                  unbilled work as of {data.projected.estimateAsOf}{' '}
                  (timetoinvoice)
                  {estimateAgeDays(data.projected.estimateAsOf) >
                    ESTIMATE_STALE_AFTER_DAYS && ' — stale'}
                </span>
              </span>
            )}
          </div>
          <div className="rt-hero__block">
            <span className="rt-hero__label">
              {isHistoric
                ? varianceToTarget >= 0
                  ? 'Above target'
                  : 'Short of target'
                : 'Still to invoice'}
            </span>
            <span className="rt-hero__value">
              {formatCurrency(
                isHistoric ? Math.abs(varianceToTarget) : tracking.gapToTarget
              )}
            </span>
            <span className="rt-hero__hint">
              {isHistoric ? (
                <>
                  Net profit before tax{' '}
                  {formatCurrency(tracking.qtdNetProfit)}
                </>
              ) : (
                <>
                  &asymp; {formatCurrency(tracking.requiredPerWeek)}/week for{' '}
                  {tracking.weeksRemaining.toFixed(1)} weeks
                </>
              )}
            </span>
          </div>
          <span className={`rt-status ${status.className}`}>
            {status.label}
          </span>
        </div>

        <div
          className="rt-progress"
          role="progressbar"
          aria-valuenow={Math.min(100, Math.max(0, Math.round(tracking.revenueProgressPercent)))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Revenue progress against target"
        >
          <div
            className="rt-progress__fill"
            style={{ width: `${progressWidth}%` }}
          />
          <div
            className="rt-progress__time-marker"
            style={{ left: `${timeMarker}%` }}
            title={`${tracking.timeElapsedPercent.toFixed(0)}% of the quarter elapsed`}
          />
        </div>
        <div className="rt-progress__legend">
          <span>
            <span className="rt-legend-swatch rt-legend-swatch--fill" />
            Revenue invoiced
          </span>
          <span>
            <span className="rt-legend-swatch rt-legend-swatch--marker" />
            Time elapsed ({tracking.timeElapsedPercent.toFixed(0)}%)
          </span>
        </div>
      </section>

      {/* Margin check */}
      <section
        className={`rt-margin-check ${
          isHistoric
            ? actualMarginPercent >= config.targetNetMarginPercent
              ? ''
              : 'rt-margin-check--warn'
            : targetCoversMargin
              ? ''
              : 'rt-margin-check--warn'
        }`}
        aria-label="Margin check"
      >
        {isHistoric ? (
          <p>
            The quarter closed on {formatCurrency(tracking.qtdRevenue)} revenue
            and {formatCurrency(tracking.qtdCosts)} costs, for a net profit
            before tax of{' '}
            <strong>{formatCurrency(tracking.qtdNetProfit)}</strong> (
            {actualMarginPercent.toFixed(1)}% margin) against a{' '}
            {config.targetNetMarginPercent}% goal.
          </p>
        ) : targetCoversMargin ? (
          <p>
            At {formatCurrency(tracking.targetRevenue)} revenue and estimated
            quarter costs of {formatCurrency(tracking.quarterCostEstimate)},
            projected net profit is{' '}
            <strong>{formatCurrency(tracking.projectedNetProfitAtTarget)}</strong>{' '}
            ({tracking.projectedMarginAtTarget.toFixed(1)}% margin) — meets the{' '}
            {config.targetNetMarginPercent}% goal.
          </p>
        ) : (
          <p>
            The current target of {formatCurrency(tracking.targetRevenue)}{' '}
            yields {tracking.projectedMarginAtTarget.toFixed(1)}% margin on
            estimated costs of {formatCurrency(tracking.quarterCostEstimate)}.
            To reach {config.targetNetMarginPercent}% you need{' '}
            <strong>{formatCurrency(tracking.requiredRevenueForMargin)}</strong>{' '}
            — or reduce costs by{' '}
            <strong>
              {formatCurrency(
                tracking.quarterCostEstimate -
                  tracking.targetRevenue *
                    (1 - config.targetNetMarginPercent / 100)
              )}
            </strong>
            .
          </p>
        )}
      </section>

      {/* Monthly breakdown */}
      <section className="rt-section" aria-label="Monthly breakdown">
        <h3 className="rt-section__title">Quarter by month</h3>
        <div className="rt-table-wrap">
          <table className="rt-table">
            <thead>
              <tr>
                <th scope="col">Month</th>
                <th scope="col">Revenue</th>
                <th scope="col">Costs</th>
                <th scope="col">Net profit</th>
                <th scope="col">Basis</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month} className={`rt-table__row--${m.status}`}>
                  <td>{m.label}</td>
                  <td>
                    {m.status === 'estimated' && !m.estimate
                      ? '—'
                      : m.status === 'estimated'
                        ? ''
                        : formatCurrency(m.revenue)}
                    {m.estimate && (
                      <span className="rt-estimate">
                        {m.status !== 'estimated' &&
                          `+ ${formatCurrency(m.estimate.unbilledSubtotal)} unbilled → `}
                        est. {formatCurrency(m.estimate.estimatedTotal)}
                        {m.estimate.failedCount > 0 && (
                          <span
                            className="rt-estimate__warn"
                            role="img"
                            aria-label={`${m.estimate.failedCount} project(s) failed to price — excluded, so the estimate understates`}
                            title={`${m.estimate.failedCount} project(s) failed to price — excluded, so the estimate understates`}
                          >
                            &#9888;
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td>{formatCurrency(m.costs)}</td>
                  <td
                    className={
                      m.status !== 'estimated' && m.netProfit < 0
                        ? 'rt-negative'
                        : ''
                    }
                  >
                    {m.status === 'estimated'
                      ? '—'
                      : formatCurrency(m.netProfit)}
                  </td>
                  <td>
                    <span className={`rt-badge rt-badge--${m.status}`}>
                      {MONTH_STATUS_LABEL[m.status]}
                    </span>
                    {m.estimate && (
                      <span className="rt-estimate__asof">
                        Est. as of {m.estimate.asOf}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="rt-table__totals">
                <td>{isHistoric ? 'Quarter totals' : 'QTD totals'}</td>
                <td>{formatCurrency(tracking.qtdRevenue)}</td>
                <td>{formatCurrency(tracking.qtdCosts)}</td>
                <td className={tracking.qtdNetProfit < 0 ? 'rt-negative' : ''}>
                  {formatCurrency(tracking.qtdNetProfit)}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Estimate vs actual track record (hidden when empty) */}
      <BillingEstimateHistorySection history={data.estimateHistory ?? []} />

      {/* Cost basis */}
      <section className="rt-section" aria-label="Cost basis">
        <h3 className="rt-section__title">
          Monthly cost base — {formatCurrency(costBasis.totalMonthly)}/month
        </h3>
        <p className="rt-section__note">
          Averaged over {costBasis.basisMonths.join(', ')}. Labour uses the
          latest completed month ({costBasis.labourSourceMonth}) to reflect
          current headcount.
        </p>
        <div className="rt-basis-grid">
          <div className="rt-basis-item">
            <span className="rt-basis-item__label">Salaries &amp; super (current)</span>
            <span className="rt-basis-item__value">
              {formatCurrency(costBasis.labourMonthly)}
            </span>
          </div>
          <div className="rt-basis-item">
            <span className="rt-basis-item__label">Contractors (4-mo avg)</span>
            <span className="rt-basis-item__value">
              {formatCurrency(costBasis.contractorMonthlyAvg)}
            </span>
          </div>
          <div className="rt-basis-item">
            <span className="rt-basis-item__label">Other overheads (4-mo avg)</span>
            <span className="rt-basis-item__value">
              {formatCurrency(costBasis.otherOpexMonthlyAvg)}
            </span>
          </div>
          <div className="rt-basis-item">
            <span className="rt-basis-item__label">Subscriptions baseline</span>
            <span className="rt-basis-item__value">
              {formatCurrency(costBasis.subscriptionsBaselineMonthly)}
            </span>
          </div>
          <div className="rt-basis-item rt-basis-item--ai">
            <span className="rt-basis-item__label">AI budget (target)</span>
            <span className="rt-basis-item__value">
              {formatCurrency(costBasis.aiBudgetMonthly)}
            </span>
            <span className="rt-basis-item__hint">
              Last month subscriptions actual:{' '}
              {formatCurrency(costBasis.subscriptionsLastMonth)} — reduce AI
              spend to hold this budget
            </span>
          </div>
        </div>
      </section>

      {/* Forward-looking modelling only applies to the live quarter */}
      {!isHistoric && (
        <>
          {/* Cost per person (owner-only salary register) */}
          <CostPerPersonSection
            onRegisterChanged={() => setScenarioRefreshToken((t) => t + 1)}
          />

          {/* Profit scenarios */}
          <ProfitScenariosSection refreshToken={scenarioRefreshToken} />

          {/* Asset purchases (capex modelling) */}
          <AssetPurchaseSection />
        </>
      )}

      {/* Settings */}
      <RevenueTargetSettings
        config={config}
        requiredRevenueForMargin={tracking.requiredRevenueForMargin}
        saving={saving}
        saveError={saveError}
        onSave={handleSaveConfig}
      />
    </div>
  );
}
